import React, { useEffect, useState } from "react";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import { IconButton, makeStyles, Tooltip } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

type DateTimePickerProps = {
  value?: Date | null;
  onChange?: (val: any) => void;
  onClear?: () => void;
  label: string;
  dataTest?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  startAdornment?: JSX.Element;
  required?: boolean;
  minDate?: Date;
};

const useStyles = makeStyles((theme) => ({
  readOnlyInput: {},
}));

const DATE_TIME_FORMAT = "dd/MM/yyyy HH:mm";

export const DateTimePicker = React.forwardRef<unknown, DateTimePickerProps>(
  (
    {
      value,
      onChange,
      onClear,
      label,
      dataTest,
      readOnly,
      disabled,
      className,
      startAdornment,
      required,
      minDate,
    },
    ref
  ) => {
    const classes = useStyles();

    const [localValue, setLocalValue] = useState(value || null);
    useEffect(() => {
      // once we apply changes (or something external has changed the search ?), refresh
      //  our working params from the source of truth
      setLocalValue(value || null);
    }, [value]);

    const handleClear = () => {
      if (onClear) {
        setLocalValue(null);
        onClear();
      }
    };

    const handleLocalChange = (val: MaterialUiPickersDate) => {
      setLocalValue(val);
      if (!val && onClear) {
        onClear();
      } else if (onChange && val && !isNaN(val.getTime())) {
        const pickerResult = val.toISOString();
        onChange(pickerResult);
      }
    };

    return (
      <>
        <KeyboardDateTimePicker
          className={className}
          fullWidth
          disableToolbar
          ampm={false}
          label={label}
          variant="inline"
          margin="none"
          format={DATE_TIME_FORMAT}
          value={localValue}
          onChange={handleLocalChange}
          KeyboardButtonProps={{
            "aria-label": `change ${label}`,
          }}
          helperText=""
          data-test={dataTest}
          InputProps={{
            readOnly,
            startAdornment,
            className: readOnly ? classes.readOnlyInput : undefined,
          }}
          disabled={disabled}
          InputAdornmentProps={
            startAdornment
              ? {
                  position: "end",
                  style: { order: 2, marginLeft: 0 },
                }
              : undefined
          }
          inputVariant="outlined"
          readOnly={readOnly}
          required={required}
          minDate={minDate || undefined}
        />
        {onClear ? (
          <Tooltip title="Clear">
            <IconButton onClick={handleClear} size="small">
              <Clear />
            </IconButton>
          </Tooltip>
        ) : null}
      </>
    );
  }
);
